import { Resources } from 'constants/resources';

import React, { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CommissionsFormValues } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/components/MerchantShowCommissionsForm/MerchantShowCommissionsForm';
import { COMMISSIONS_SCHEMA } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/schemas/schema';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Form, RecordContextProvider, useNotify, useUpdate } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { Button, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Commission } from 'types/embedded/commission';

import { MerchantShowCommissionsEditDialogForm } from './MerchantShowCommissionsEditDialogForm';
import { transform } from '../utils/transform';

type Props = {
  open: boolean;
  onClose: () => void;
  merchantId: string | undefined;
  record: Commission | undefined;
};

export const MerchantShowCommissionsEditDialog: React.FC<Props> = ({
  open,
  onClose,
  merchantId,
  record,
}) => {
  const translate = useTypedTranslate();
  const [update] = useUpdate();
  const [errors, setErrors] = useState<string[]>([]);
  const notify = useNotify();

  const onSubmit = async (data: CommissionsFormValues) => {
    const transformedData = transform(data);

    try {
      await update(
        Resources.Commissions,
        {
          id: record?.id,
          data: {
            ...transformedData,
            merchant: merchantId,
          },
          previousData: record,
        },
        { returnPromise: true },
      );

      notify(translate('app.merchantCommissions.itemUpdated'), {
        type: 'success',
      });
      onClose();
    } catch (error: any) {
      notify(`${translate('app.common.error')}: ${(error as Error).message}`, {
        type: 'error',
      });
      setErrors(error?.message.split('\n'));
    }
  };

  return (
    <Dialog maxWidth="lg" onClose={onClose} open={open} width="1200px">
      <RecordContextProvider value={record}>
        <Form
          defaultValues={{ merchant: merchantId }}
          onSubmit={onSubmit as any}
          resolver={yupResolver<FieldValues>(COMMISSIONS_SCHEMA)}
        >
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title={translate('app.merchantCommissions.feeEditing')}
            titleSize="smallBold"
          />
          <MerchantShowCommissionsEditDialogForm errors={errors} merchantId={merchantId} />
          <CardActionsButtons
            rightActionsSlot={[
              <Button
                key="cancel"
                label={translate('app.buttons.cancel')}
                onClick={onClose}
                variant="outlined"
              />,
              <SaveButton key="update" label={translate('app.buttons.save')} />,
            ]}
          />
        </Form>
      </RecordContextProvider>
    </Dialog>
  );
};
